import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { distinctUntilChanged, filter, Observable, Subject, take, takeUntil, debounceTime } from 'rxjs';
import { CatalogsService } from 'src/app/features/catalogs/services/catalogs.services';
import { IProduct, IProductCurrency } from 'src/app/features/produits/models/products.models';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ProductDetailsDialogComponent } from './product-details-dialog/product-details-dialog.component';
import { IPagination } from 'src/app/shared/models/page.model';
import { ICatalog, IGeneralRules } from 'src/app/features/catalogs/models/catalogs.models';
import { CustomeOverrideComponent } from '../../../custome-override/custome-override.component';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { TranslateService } from '@ngx-translate/core';
import { areSelectedProductsValid, getGlobalValidationMessage, getValidationTooltipMessage } from 'src/app/shared/validators/products-prices-validators';
import { PricebookService } from 'src/app/features/pricebook/services/pricebook.service';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { IPriceBook, IPriceBookViewDTO } from 'src/app/features/pricebook/models/pricebook.model';

interface ProductListOptions {
  triggerPage: 'catalog-edit' | 'catalog-view';
  title?: string;
  filterIsActive?: boolean;
  pricingRules?: IPricingRules;
}

interface IPricingRules {
  pricing_rule: 'discount' | 'margin' | null;
  overall_pricing_value: number;
  extra_discount: number | null;
}

@Component({
  selector: 'app-selecteable-products-list',
  templateUrl: './selecteable-products-list.component.html',
  styleUrls: ['./selecteable-products-list.component.scss'],
})
export class SelecteableProductsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() source: 'agents' | 'catalogs' = 'agents';
  @Input() products: IProduct[] = [];

  @Input() productsMetadata: IPagination | null = null;
  @Input() initialSelectedProducts: IProduct[] = [];
  @Input() selectedProductIds: Set<number> = new Set();
  @Input() currencyControl = new FormControl();
  @Input() isLoadingMore = false;
  @Input() isSelectingAll = false;
  @Input() hasSelectedAllProducts = false;
  @Input() canSubmit = true;
  @Input() options: ProductListOptions = {
    triggerPage: 'catalog-edit',
    title: 'Catalogs.new_catalog.products.listing.title',
    filterIsActive: false,
    pricingRules: {
      pricing_rule: 'discount',
      overall_pricing_value: 0,
      extra_discount: null,
    },
  };

  @Output() selectedProductsChange = new EventEmitter<IProduct[]>();
  @Output() openFilter = new EventEmitter<void>();
  @Output() clearFilter = new EventEmitter<void>();
  @Output() loadMore = new EventEmitter<void>();
  @Output() areSelectedProductsValidEmitter = new EventEmitter<boolean>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() selectAllRequested = new EventEmitter<void>();
  @Output() deselectAllRequested = new EventEmitter<void>();
  
  catalog$: Observable<ICatalog | null> = this.store.select((state) => state.catalogs.catalog);
  catalog: ICatalog | null = null;

  filteredProducts: IProduct[] = [];
  hasMore = false;
  private scrollSubject = new Subject<Event>();
  private lastScrollEmit = 0;

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  private scrollThreshold = 0.9;
  selectedProducts: IProduct[] = [];
  searchInput = new FormControl('');

  activeSortKey: string | null = null;
  activeSortMode: 'asc' | 'desc' | null = null;

  baseUrl = environment.BASE_URL_API;
  placeholder = 'assets/images/placeholder.png';
  timestamp = new Date().getTime();
  failedImages = new Set<string>();

  newCatalogProducts: IProduct[] = [];
  newCatalogPricingRules: IPricingRules = {
    pricing_rule: 'discount',
    overall_pricing_value: 0,
    extra_discount: 0,
  };

  currencies$: Observable<ICurrency[]> = this.store.select((state) => state.customers.allCurrencies);
  availableCurrencies: ICurrency[] = [];
  defaultCurrency: number | null = null;
  isMultiCurrency: boolean = false;
  showFullPricingData: boolean = true;
  quantityRules: IGeneralRules | null = null;

  hasExtraDiscount: Boolean = false;

  productsForm!: FormGroup;

  destroy$ = new Subject<void>();

  // Initialize product currencies based on availableCurrencies
  initializeProductCurrencies(product: IProduct): void {
    if (!this.availableCurrencies.length) return;

    // Create a map of existing currencies for quick lookup
    const existingCurrencies = new Map((product.currencies || []).map((c) => [c.currency_id, c]));

    // Fallback sales_price for new currencies (so final_price can be computed)
    const fallbackSalesPrice =
      product.sales_price ??
      (product.currencies || []).find((c) => c.sales_price != null)?.sales_price;

    // Merge: keep all existing product currencies, and add any missing available currencies
    const merged = [...(product.currencies || [])];
    for (const availableCurrency of this.availableCurrencies) {
      if (!existingCurrencies.has(availableCurrency.id)) {
        merged.push({
          currency_id: availableCurrency.id,
          currency_name: availableCurrency.name,
          sales_price: fallbackSalesPrice ?? undefined,
          final_price: undefined,
        });
      }
    }
    product.currencies = merged;
  }

   // Initialize currencies for all products
   initializeAllProductsCurrencies(): void {
    if (!this.availableCurrencies.length) return;

    this.products.forEach((product) => {
      this.initializeProductCurrencies(product);
    });

    // Note: filteredProducts contains references to the same objects as products,
    // so initializing products is sufficient
  }

  // Helper method to get currency-specific price
  getCurrencyPrice(product: IProduct, priceType: 'sales_price' | 'final_price'): number | null {
    if (!this.currencyControl.value || !product.currencies) {
      const fallbackPrice = priceType === 'sales_price' ? product.sales_price : product.final_price;
      return fallbackPrice ?? null;
    }

    const currency = product.currencies.find((c) => c.currency_id === this.currencyControl.value);
    if (!currency) {
      return null;
    }

    return currency[priceType] ?? null;
  }

  // Helper method to get currency-specific cost
  getCurrencyCost(product: IProduct): number | null {
    if (!this.currencyControl.value || !product.currencies) {
      return product.cost ?? null;
    }

    const currency = product.currencies.find((c) => c.currency_id === this.currencyControl.value);
    if (!currency) {
      return product.cost ?? null;
    }

    return currency.cost ?? product.cost ?? null;
  }

  // Helper method to set currency-specific price
  setCurrencyPrice(product: IProduct, priceType: 'sales_price' | 'final_price', value: number): void {
    if (!this.currencyControl.value) {
      if (priceType === 'sales_price') {
        product.sales_price = value;
      } else {
        product.final_price = value;
      }
      return;
    }

    if (!product.currencies) {
      product.currencies = [];
    }

    // Find and update the currency
    const currencyIndex = product.currencies.findIndex((c) => c.currency_id === this.currencyControl.value);
    if (currencyIndex !== -1) {
      product.currencies = [...product.currencies].map((c, index) => (index === currencyIndex ? { ...c, [priceType]: value } : c));
    }
  }

  // Update final prices for all currencies based on discount
  updateAllCurrenciesFinalPrice(product: IProduct, discount: number): IProduct {
    if (!product.currencies) return product;

    product.currencies = [...product.currencies].map((currency) => {
      if (currency.sales_price) {
        const finalPrice = currency.sales_price * (1 - discount / 100);
        return { ...currency, final_price: Number(finalPrice.toFixed(2)) };
      }
      return currency;
    });
    return product;
  }

  // Update form controls when currency changes
  updateFormControlsForCurrencyChange(): void {
    this.products.forEach((product: IProduct) => {
      const id = this.getProductFormId(product);
      if (id == null) return;
      const salesPrice = this.getCurrencyPrice(product, 'sales_price');
      const finalPrice = this.getCurrencyPrice(product, 'final_price');

      // Update final price control
      const finalPriceControl = this.productsForm.get(`final_price_${id}`);
      if (finalPriceControl) {
        if (finalPrice === null && salesPrice) {
          finalPriceControl.setValue(salesPrice);
        } else {
          finalPriceControl.setValue(finalPrice || 0);
        }
      }

      // Update discount control - use product.discount if available, otherwise calculate
      const discountControl = this.productsForm.get(`discount_${id}`);
      if (discountControl) {
        if (salesPrice) {
          discountControl.setValue(this.normalizeDiscount(product.discount ?? (finalPrice ? Number(100 - (finalPrice * 100) / salesPrice) : 0)));

          // Update validators based on pricing rule: allow negative values for margin
          const isMargin = this.newCatalogPricingRules.pricing_rule === 'margin';
          discountControl.setValidators(isMargin 
            ? [Validators.required]
            : [Validators.required, Validators.min(0), Validators.max(100)]);
        } else {
          // No sales price, clear validators
          discountControl.setValidators([]);
          discountControl.setValue(0);
        }
        discountControl.updateValueAndValidity();
      }
    });
  }

  constructor(
    private catalogService: CatalogsService,
    public dialog: MatDialog,
    private store: Store<AppState>,
    private translateService: TranslateService,
    private pricebookService: PricebookService,
    private cdr: ChangeDetectorRef,
    public featureFlags: FeatureFlagService
  ) {
    // Listen to currency changes
    this.currencyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateFormControlsForCurrencyChange();
    });

    // Debounce scroll events to prevent too frequent loadMore calls
    this.scrollSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      this.handleScroll(event);
    });
  }

  ngOnInit(): void {
    this.productsForm = new FormGroup({});

    this.catalog$.pipe(takeUntil(this.destroy$)).subscribe((catalog) => {
      this.catalog = catalog;
    });

    // Store previous overall_pricing_value to detect changes
    let previousOverallPricingValue: number | null = null;

    // Listen to data change
    this.catalogService.newCatalogData$
      .pipe(
        filter((data) => data != null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        if (data) {
          this.newCatalogPricingRules = {
            pricing_rule: data.pricing_rule,
            overall_pricing_value: data.overall_pricing_value,
            extra_discount: data.extra_discount || 0,
          };

          this.hasExtraDiscount = data.has_extra_discount || false;
          this.quantityRules = data.quantity_rules || null;
          this.isMultiCurrency = data.is_multi_currency || false;
          this.showFullPricingData = data.show_full_pricing_data ?? true;
          this.defaultCurrency = data.default_currency || null;
          this.currencies$.pipe(take(1)).subscribe((list) => {
            this.availableCurrencies = list.filter((currency) => data.enabled_currencies?.includes(currency.id)) || [];
            // add default currency to availableCurrencies if not already in the list
            if (!this.availableCurrencies.some((currency) => currency.id === data.default_currency)) {
              if (data.default_currency) {
                this.availableCurrencies.push({
                  id: data.default_currency,
                  name: list.find((currency) => currency.id === data.default_currency)?.name || '',
                  full_name: list.find((currency) => currency.id === data.default_currency)?.full_name || '',
                  symbol: list.find((currency) => currency.id === data.default_currency)?.symbol || '',
                  is_default: list.find((currency) => currency.id === data.default_currency)?.is_default || false,
                  is_active: list.find((currency) => currency.id === data.default_currency)?.is_active || false,
                });
              }
            }
            // Initialize currencies for all products when availableCurrencies is loaded
            this.initializeAllProductsCurrencies();
          });
          // Set currency control state based on multi-currency setting
          this.currencyControl.setValue(data.default_currency || null, { emitEvent: false });
          if (!this.isMultiCurrency) {
            this.currencyControl.disable({ emitEvent: false });
          } else {
            this.currencyControl.enable({ emitEvent: false });
          }
          // set default discount for all products
          this.filteredProducts.forEach((product: IProduct) => {
            if (!product.discount) {
              product.discount = this.normalizeDiscount(data.overall_pricing_value || 0);
            }
          });
          
          const currentOverallPricingValue = data.overall_pricing_value || 0;
          const overallPricingValueChanged = previousOverallPricingValue !== null && previousOverallPricingValue !== currentOverallPricingValue;
          
          if (overallPricingValueChanged && data.products && data.products.length > 0) {
            this.selectedProducts = this.selectedProducts.map((selectedProduct) => {
              const selectedKey = selectedProduct.id ?? selectedProduct.product_id;
              const updatedProduct = data.products.find((p: IProduct) => (p.id ?? p.product_id) === selectedKey);
              if (updatedProduct) {
                // Check if product has cost (for margin mode)
                const hasCost = this.newCatalogPricingRules.pricing_rule === 'margin'
                  ? (this.getCurrencyCost(selectedProduct) ?? 0) > 0
                  : true;
                
                if (this.newCatalogPricingRules.pricing_rule === 'margin' && !hasCost) {
                  return selectedProduct;
                }
                return {
                  ...updatedProduct,
                  hasCustomDiscount: false
                };
              }
              return selectedProduct;
            });
            
            // Update form controls to reflect the new global percentage
            this.selectedProducts.forEach((product: IProduct) => {
              const hasCost = this.newCatalogPricingRules.pricing_rule === 'margin'
                ? (this.getCurrencyCost(product) ?? 0) > 0
                : true;
              
              // Skip updating products without cost in margin mode
              if (this.newCatalogPricingRules.pricing_rule === 'margin' && !hasCost) {
                return;
              }
              
              const formId = this.getProductFormId(product);
              const discountControl = formId != null ? this.productsForm.get(`discount_${formId}`) : null;
              if (discountControl) {
                discountControl.setValue(product.discount, { emitEvent: false });
              }
              
              // Update final_price control if in margin mode
              if (this.newCatalogPricingRules.pricing_rule === 'margin') {
                const finalPriceControl = formId != null ? this.productsForm.get(`final_price_${formId}`) : null;
                if (finalPriceControl && product.final_price) {
                  finalPriceControl.setValue(product.final_price, { emitEvent: false });
                }
              }
            });
            this.emitSelectedProducts();
          }
          previousOverallPricingValue = currentOverallPricingValue;
        }
      });

    const data = this.catalogService.getNewCatalogData();

    if (this.options.triggerPage === 'catalog-edit') {
      if (data) {
        this.newCatalogProducts = (data.products || []).map((product: IProduct) => ({
          ...product,
          hasCustomDiscount: (product as any).hasCustomDiscount
        }));
        this.initialSelectedProducts = this.newCatalogProducts.map((product: IProduct) => ({
          ...product,
          hasCustomDiscount: (product as any).hasCustomDiscount
        })) || [];
      }
      this.selectedProducts = this.initialSelectedProducts ? [...this.initialSelectedProducts.map((product: IProduct) => ({
        ...product,
        hasCustomDiscount: (product as any).hasCustomDiscount
      }))] : [];
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.source === 'catalogs' || this.source === 'agents') {
      const selectedByKey = new Map<number, IProduct>((this.initialSelectedProducts || []).map((p) => [(p.id ?? p.product_id)!, p]));
      this.products = this.products.map((product) => {
        const rowKey = product.id ?? product.product_id;
        const selected = rowKey != null ? selectedByKey.get(rowKey) : undefined;
        return {
          ...product,
          min_qte:
            selected?.min_qte! > 1
              ? selected?.min_qte
              : this.quantityRules && this.quantityRules!.min_qte > 1
              ? this.quantityRules!.min_qte
              : product.min_qte,
          max_qte: selected?.max_qte
            ? selected?.max_qte
            : this.quantityRules && this.quantityRules!.max_qte
            ? this.quantityRules!.max_qte
            : product.max_qte,
          increment: selected?.increment
            ? selected?.increment
            : this.quantityRules && this.quantityRules!.increment > 1
            ? this.quantityRules!.increment
            : product.increment,
          can_be_sold_out_of_stock: selected?.can_be_sold_out_of_stock
            ? selected?.can_be_sold_out_of_stock
            : this.quantityRules && this.quantityRules!.can_be_sold_out_of_stock
            ? this.quantityRules!.can_be_sold_out_of_stock
            : product.can_be_sold_out_of_stock,
          currencies: selected?.currencies ? selected?.currencies : product.currencies,
        };
      });
    }

    if (changes['products']) {
      this.initializeAllProductsCurrencies();
      this.filteredProducts = [...this.products];

      if (this.options.triggerPage === 'catalog-edit') {
        this.products.forEach((product) => {
          const formId = this.getProductFormId(product);
          if (formId != null && !this.productsForm?.contains(`final_price_${formId}`)) {
            this.createControlsForProduct(product);
          }
        });
        this.autoSelectProductsByIds(this.selectedProductIds);
        setTimeout(() => this.emitSelectedProducts(), 0);
      }
      this.updateHasMore();
    }

    if (changes['selectedProductIds']) {
      if (this.selectedProductIds.size > 0) {
        this.autoSelectProductsByIds(this.selectedProductIds);
      }
      this.cdr.markForCheck();
    }

    if (changes['productsMetadata']) {
      this.productsMetadata = changes['productsMetadata'].currentValue;
      this.updateHasMore();
    }

    if (changes['isLoadingMore'] && !this.isLoadingMore && changes['isLoadingMore'].previousValue) {
      this.updateHasMore();
    }

    if (changes['hasSelectedAllProducts']) {
      // When "select all" is activated, ensure visible products are immediately selected
      if (this.hasSelectedAllProducts) {
        // Get all visible valid product IDs
        const visibleProductIds = this.filteredProducts
          .filter((product: IProduct) => this.isProductValid(product))
          .map((product: IProduct) => (product.id ?? product.product_id))
          .filter((id): id is number => id != null && id > 0);
        
        // Add visible product IDs to selectedProductIds if they're not already there
        // This ensures the checkbox shows as checked immediately
        if (visibleProductIds.length > 0) {
          const updatedIds = new Set(this.selectedProductIds);
          visibleProductIds.forEach(id => updatedIds.add(id));
          this.selectedProductIds = updatedIds;
        }
        
        // Auto-select visible products that are in selectedProductIds
        if (this.selectedProductIds.size > 0) {
          this.autoSelectProductsByIds(this.selectedProductIds);
        }
      }
      // Trigger change detection when "select all" state changes to update checkbox
      this.cdr.markForCheck();
    }
  }

  /**
   * Normalize discount value to handle floating point precision errors.
   * Values very close to zero (less than 0.0001) are rounded to 0.
   * Other values are rounded to 2 decimal places.
   */
  private normalizeDiscount(discount: number): number {
    try {
      discount = Number(discount);
      if (isNaN(discount)) { return 0; }
    }
    catch { return 0; }
    return Number(discount.toFixed(2));
  }

  getProductFormId(product: IProduct): number | undefined {
    return product.id ?? product.product_id;
  }

  getMarginDisplay(product: IProduct): string {
    const formId = this.getProductFormId(product);
    if (formId == null) return '--';
    const control = this.productsForm?.get(`discount_${formId}`);
    const val = control?.value;
    if (val != null && val !== '') return val + '%';
    if (product.discount != null && product.discount !== undefined) return product.discount + '%';
    return '--';
  }

  // Helper to create controls for a product
  createControlsForProduct(product: IProduct) {
    // Ensure productsForm is initialized
    if (!this.productsForm) {
      this.productsForm = new FormGroup({});
    }
    
    const id = product.id ?? product.product_id;
    if (id == null) return;
    const form = this.productsForm;
    let extra_discount = this.newCatalogPricingRules.extra_discount || 0;
    let discountValue = this.newCatalogPricingRules.overall_pricing_value || 0;
    let finalPriceValue = 0;
    let salesPrice = 0;
    if (this.source === 'agents') {
      if (this.options.triggerPage === 'catalog-edit') {
        salesPrice = this.getCurrencyPrice(product, 'sales_price') || 0;
        const cost = product.cost ?? 0;
        if (this.newCatalogPricingRules.pricing_rule === 'margin' && !(cost > 0)) {
          finalPriceValue = 0;
        } else {
          finalPriceValue = this.getCurrencyPrice(product, 'final_price') ?? 0;
        }
      } else {
        salesPrice = product.sales_price || 0;
        if (this.newCatalogPricingRules.pricing_rule === 'margin') {
          const cost = product.cost ?? 0;
          finalPriceValue = cost > 0 ? +(cost * (1 + discountValue / 100)).toFixed(2) : 0;
        } else {
          finalPriceValue = salesPrice ? +(salesPrice * (discountValue ? 1 - discountValue / 100 : 1)).toFixed(2) : 0;
        }
      }
    } else {
      salesPrice = this.getCurrencyPrice(product, 'sales_price') || 0;
      if (this.newCatalogPricingRules.pricing_rule === 'margin') {
        const cost = this.getCurrencyCost(product) || 0;
        if (cost && cost > 0) {
          finalPriceValue = +(cost * (1 + discountValue / 100)).toFixed(2);
        } else {
          finalPriceValue = 0;
        }
      } else {
        finalPriceValue = salesPrice ? +(salesPrice * (discountValue ? 1 - discountValue / 100 : 1)).toFixed(2) : 0 || 0;
      }
    }

    // If Catalog already have some products selected, reuse those products values as default (final_price, discount, extra_discount).
    if (this.newCatalogProducts.length > 0) {
      const existingProduct = this.newCatalogProducts.find((p) => (p.id ?? p.product_id) === id);

      if (existingProduct && existingProduct.extra_discount) {
        extra_discount = existingProduct.extra_discount;
      }
      else if (existingProduct && existingProduct.max_extra_discount) {
        extra_discount = existingProduct.max_extra_discount;
      }

      if (existingProduct) {
        // Check if product has custom discount (individually modified)
        const hasCustomDiscount = (existingProduct as any).hasCustomDiscount;
        
        if (this.source === 'agents' && existingProduct.discount) {
          discountValue = this.normalizeDiscount(existingProduct.discount);
        } else if (this.source === 'catalogs') {
          // If product has custom discount, prioritize existingProduct.discount
          if (hasCustomDiscount && existingProduct.discount !== null && existingProduct.discount !== undefined) {
            discountValue = existingProduct.discount;
          } else if (product.discount) {
            discountValue = this.normalizeDiscount(product.discount);
          } else {
            // Calculate discount from final price only if no custom discount
            const finalPrice = this.getCurrencyPrice(product, 'final_price') || 0;
            const salesPrice = this.getCurrencyPrice(product, 'sales_price') || 0;
            
            // If pricing rule is margin, calculate margin from cost and final_price
            if (this.newCatalogPricingRules.pricing_rule === 'margin') {
              const cost = this.getCurrencyCost(product) || 0;
              if (cost && cost > 0 && finalPrice > 0) {
                discountValue = Number(((finalPrice - cost) / cost * 100).toFixed(2));
              } else {
                discountValue = 0;
              }
            } else {
              discountValue = this.normalizeDiscount(salesPrice ? Number(100 - (finalPrice * 100) / salesPrice) : 0);
            }
          }
        }
      }

      if (existingProduct) {
        if (this.source === 'agents' && (existingProduct.final_price !== undefined && existingProduct.final_price !== null)) {
          finalPriceValue = existingProduct.final_price;
        } else if (this.source === 'catalogs') {
          finalPriceValue = this.getCurrencyPrice(product, 'final_price') ?? 0;
        }
      }
    }

    if (!form.contains(`final_price_${id}`)) {
      form.addControl(`final_price_${id}`, new FormControl(finalPriceValue, [Validators.required, Validators.min(0)]));
    }

    if (!form.contains(`discount_${id}`)) {
      // For margin pricing rule, allow negative values. For discount, min is 0 and max is 100
      const isMargin = this.newCatalogPricingRules.pricing_rule === 'margin';
      const validators = salesPrice 
        ? (isMargin 
          ? [Validators.required]
          : [Validators.required, Validators.min(0), Validators.max(100)])
        : [];
      form.addControl(`discount_${id}`, new FormControl(this.normalizeDiscount(discountValue), validators));
    }

    if (!form.contains(`extra_discount_${id}`) && this.newCatalogPricingRules.extra_discount != null) {
      form.addControl(`extra_discount_${id}`, new FormControl(extra_discount, [Validators.required, Validators.min(0), Validators.max(100)]));
    }

    product.discount = this.normalizeDiscount(discountValue);
  }

  trackByProductId(_index: number, product: IProduct): number {
    return product.id ?? product.product_id ?? 0;
  }

  getImage(image: string): string {
    return this.baseUrl + image + '?thumbnail=1&timestamp=' + this.timestamp;
  }

  getFinalPrice(product: IProduct): number | null {
    let salesPrice = 0;
    if (this.source === 'agents') {
      salesPrice = product.sales_price || 0;
    } else {
      salesPrice = this.getCurrencyPrice(product, 'sales_price') || 0;
    }
    const formId = this.getProductFormId(product);
    const discount = product.discount ?? (formId != null ? (this.productsForm.get(`discount_${formId}`)?.value || 0) : 0);
    return salesPrice ? +(salesPrice * (1 - discount / 100)).toFixed(2) : 0;
  }

  getGifts() {
    const gifts = [
      ...(this.catalog?.best_deal != null ? this.catalog.best_deal : []),
      this.catalog?.best_free_shipping
    ].filter((deal): deal is IPriceBookViewDTO => deal !== null);

    return gifts;
  }

  getProductGifts(product: IProduct): IPriceBookViewDTO[]{
    const gifts: IPriceBookViewDTO[] = [];

    const hasDeal = (deal: IPriceBookViewDTO) =>
      gifts.some(d => d.id === deal.id);

    const deals = product.best_deal as IPriceBookViewDTO[] | null;
    if (deals && deals.length > 0) {
      for (const deal of deals) {
        if (!hasDeal(deal)) {
          gifts.push(deal);
        }
      }
    }
    
    const buyXGetYDeal = product.best_buy_x_get_y as IPriceBookViewDTO[] | null;
    if (buyXGetYDeal && buyXGetYDeal.length > 0) {
      for (const deal of buyXGetYDeal) {
        if (!hasDeal(deal)) {
          gifts.push(deal);
        }
      }
    }

    return gifts;
  } 

  getFormControl(name: string): FormControl{
    // Ensure productsForm is initialized
    if (!this.productsForm) {
      this.productsForm = new FormGroup({});
    }
    
    // Return the control if it exists, otherwise return a temporary control
    const control = this.productsForm.get(name);
    if (control) {
      return control as FormControl;
    }
    
    // Return a temporary control to prevent template errors
    return new FormControl();
  }

  onImageError(imageUrl: string): void {
    this.failedImages.add(imageUrl);
  }

  // Remove leading zeros from input value
  onInputRemoveLeadingZero(event: Event, formGroup: FormGroup, formControlName: string) {
    if (!this.canSubmit) return;
    const input = event.target as HTMLInputElement;
    if (/^0[0-9]+/.test(input.value)) {
      input.value = input.value.replace(/^0+/, '');
      formGroup.get(formControlName)?.setValue(input.value);
    }
  }

  // Remove selected product from selected products array if input is invalid
  onInputCheckValidity(type: 'final_price' | 'discount' | 'extra_discount', product: IProduct) {
    if (!this.canSubmit) return;
    const formId = this.getProductFormId(product);
    if (formId == null) return;
    const ctrl = this.productsForm.get(`${type}_${formId}`);
    const isValid = !!ctrl && ctrl.valid;
    if (!isValid) {
      const index = this.selectedProducts.indexOf(product);
      if (index !== -1) {
        this.selectedProducts.splice(index, 1);
        this.emitSelectedProducts();
      }
    }
  }

  onDiscountChange(_event: any, product: IProduct) {
    if (!this.canSubmit) return;
    const formId = this.getProductFormId(product);
    if (formId == null) return;
    const discount = this.normalizeDiscount((this.productsForm.get(`discount_${formId}`)?.value || 0));

    // Mark this product as individually modified to preserve its custom margin/discount
    (product as any).hasCustomDiscount = true;

    // Update product discount
    product.discount = discount;

    // If pricing rule is margin, calculate final price from cost and discount (margin)
    if (this.newCatalogPricingRules.pricing_rule === 'margin') {
      const cost = this.getCurrencyCost(product) || 0;
      if (cost && discount !== null && discount !== undefined) {
        // Calculate final price from cost and margin: final_price = cost * (1 + discount / 100)
        const finalPrice = +(cost * (1 + discount / 100)).toFixed(2);
        this.productsForm.get(`final_price_${formId}`)?.setValue(finalPrice);
        product.final_price = finalPrice;
        this.setCurrencyPrice(product, 'final_price', finalPrice);
      }
    } else {
      // Update final prices for all currencies based on the new discount
      product = this.updateAllCurrenciesFinalPrice(product, discount);
      // Update the current currency's final price in the form
      const currentFinalPrice = this.getCurrencyPrice(product, 'final_price');
      this.productsForm.get(`final_price_${formId}`)?.setValue(currentFinalPrice || 0);
    }
    
    this.selectedProducts = this.selectedProducts.map((p) => ((p.id ?? p.product_id) === formId ? product : p));

      // Select the product if not already selected
    this.toggleProduct(product, true);
    this.emitSelectedProducts();
  }

  onFinalPriceChange(_event: any, product: IProduct) {
    if (!this.canSubmit) return;
    const formId = this.getProductFormId(product);
    if (formId == null) return;
    const finalPrice = this.productsForm.get(`final_price_${formId}`)?.value;
    
    // Mark this product as individually modified to preserve its custom final_price
    (product as any).hasCustomDiscount = true;
    // Update current currency's final price in the product currencies
    product.currencies = [...(product?.currencies || [])].map((currency) => {
      if (currency.currency_id === this.currencyControl.value) {
        return { ...currency, final_price: finalPrice };
      }
      return currency;
    });
    
    let discount = 0;

    // If pricing rule is margin, calculate margin from cost and final_price
    if (this.newCatalogPricingRules.pricing_rule === 'margin') {
      const cost = this.getCurrencyCost(product);
      if (cost && cost > 0) {
        // Calculate margin: margin = ((final_price - cost) / cost) * 100
        discount = Number(((finalPrice - cost) / cost * 100).toFixed(2));
        product.discount = discount;
        product.final_price = finalPrice;
        this.setCurrencyPrice(product, 'final_price', finalPrice);
      } else {
        product.discount = 0;
        product.final_price = finalPrice;
        this.productsForm.get(`discount_${formId}`)?.setValue(0);
        if (this.source === 'agents') {
        } else {
          this.setCurrencyPrice(product, 'final_price', finalPrice);
        }
      }
    } else {
      // For discount mode, calculate discount from sales_price
      const salesPrice = this.getCurrencyPrice(product, 'sales_price');
      if (salesPrice) {
        // Currency has sales price - calculate discount and update all currencies
        discount = Number(100 - (finalPrice * 100) / salesPrice);
        product.discount = this.normalizeDiscount(Number(100 - (finalPrice * 100) / salesPrice));
        product = this.updateAllCurrenciesFinalPrice(product, product.discount ?? discount);
      }
    }
    
    this.selectedProducts = this.selectedProducts.map((p) => ((p.id ?? p.product_id) === formId ? product : p));
    this.productsForm.get(`discount_${formId}`)?.setValue(discount);


    // Select the product if not already selected
    this.toggleProduct(product, true);
    this.emitSelectedProducts();
  }


  search(event: any) {
    const key = event.target.value.trim();
    // Emit search term to parent component to trigger API search
    this.searchChange.emit(key);
    // Reset sorting when search changes
    this.activeSortKey = null;
    this.activeSortMode = null;
  }

  sortByKey(key: string, sortDirection: 'asc' | 'desc'): void {
    if (key === this.activeSortKey && sortDirection === this.activeSortMode) return;
    if (!this.filteredProducts || !Array.isArray(this.filteredProducts)) return;

    const isEmpty = (v: any) => v === null || v === undefined || v === '';

    const isStrictDateString = (val: string): boolean => {
      // Matches YYYY-MM-DD, YYYY/MM/DD, with optional time (space or T separator)
      return /^\d{4}[-/]\d{2}[-/]\d{2}([ T]\d{2}:\d{2}:\d{2})?$/.test(val);
    };

    const compareValues = (a: any, b: any): number => {
      if (isEmpty(a) && isEmpty(b)) return 0;
      if (isEmpty(a)) return 1;
      if (isEmpty(b)) return -1;

      const isDateA = typeof a === 'string' && isStrictDateString(a);
      const isDateB = typeof b === 'string' && isStrictDateString(b);

      if (isDateA || isDateB) {
        return new Date(a).getTime() - new Date(b).getTime();
      }

      if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
      }

      return String(a).localeCompare(String(b));
    };

    this.filteredProducts = [...this.filteredProducts].sort((itemA: any, itemB: any) => {
      let valA: any = itemA[key];
      let valB: any = itemB[key];

      if (key === 'brand') {
        valA = itemA.brand?.name ?? '';
        valB = itemB.brand?.name ?? '';
      }

      if (key === 'stock') {
        valA = itemA.stock?.quantity ?? 0;
        valB = itemB.stock?.quantity ?? 0;
      }

      if (key === 'cost') {
        valA = itemA.cost ?? 0;
        valB = itemB.cost ?? 0;
      }

      if (this.options.triggerPage === 'catalog-edit') {
        const idA = this.getProductFormId(itemA);
        const idB = this.getProductFormId(itemB);
        if (key === 'final_price') {
          valA = idA != null ? (this.productsForm.get(`final_price_${idA}`)?.value ?? 0) : 0;
          valB = idB != null ? (this.productsForm.get(`final_price_${idB}`)?.value ?? 0) : 0;
        }

        if (key === 'discount') {
          valA = idA != null ? (this.productsForm.get(`discount_${idA}`)?.value ?? 0) : 0;
          valB = idB != null ? (this.productsForm.get(`discount_${idB}`)?.value ?? 0) : 0;
        }

        if (key === 'extra_discount') {
          valA = idA != null ? (this.productsForm.get(`extra_discount_${idA}`)?.value ?? 0) : 0;
          valB = idB != null ? (this.productsForm.get(`extra_discount_${idB}`)?.value ?? 0) : 0;
        }
      }

      const result = compareValues(valA, valB);
      return sortDirection === 'asc' ? result : -result;
    });

    this.activeSortKey = key;
    this.activeSortMode = sortDirection;
  }

  isAllProductsSelected(): boolean {
    // If in "select all" mode, show checkbox as checked (fake checkbox behavior)
    // This creates the "fake" checkbox - shows checked when select all is active
    if (this.hasSelectedAllProducts) {
      // Get all valid products from filtered list
      const allValidProducts = this.filteredProducts.filter((product: IProduct) => 
        this.isProductValid(product)
      );
      
      if (allValidProducts.length === 0) return false;
      
      // Get all valid product IDs - use same logic as parent: id ?? product_id
      const allProductsIds = allValidProducts
        .map((product: IProduct) => (product.id ?? product.product_id))
        .filter((id): id is number => id != null && id > 0);
      
      if (allProductsIds.length === 0) return false;
      
      // When "select all" is active, show as checked if all visible valid products are selected
      // Check if any visible products have been manually deselected
      const allSelected = allProductsIds.every((id: number) => 
        this.selectedProductIds.has(id)
      );
      
      // Show as checked if all visible products are selected (none manually deselected)
      // This is the "fake" checkbox - it shows checked when select all is active
      return allSelected;
    }
    
    // Normal mode: check if all visible valid products are selected
    const allValidProducts = this.filteredProducts.filter((product: IProduct) => 
      this.isProductValid(product)
    );
    
    if (allValidProducts.length === 0) return false;
    
    // Get all valid product IDs - use same logic as parent: id ?? product_id
    const allProductsIds = allValidProducts
      .map((product: IProduct) => (product.id ?? product.product_id))
      .filter((id): id is number => id != null && id > 0);
    
    // Check if all valid products are selected using selectedProductIds for accuracy
    const allSelected = allProductsIds.length > 0 && allProductsIds.every((id: number) => 
      this.selectedProductIds.has(id)
    );
    
    return allSelected;
  }

  isPartialSelectedAll(): boolean {
    if (this.hasSelectedAllProducts) {
      // If in "select all" mode, don't show partial state
      return false;
    }
    
    const allProductsIds = this.filteredProducts
      .filter((product: IProduct) => this.isProductValid(product))
      .map((product: IProduct) => product.id ?? product.product_id)
      .filter((id): id is number => id != null);
    
    const selectedCount = allProductsIds.filter((id: number) => 
      this.selectedProductIds.has(id)
    ).length;
    
    return selectedCount > 0 && selectedCount < allProductsIds.length;
  }

  areAllValidProductsSelected(): boolean {
    // If we're in "select all" mode (hasSelectedAllProducts is true), show "Deselect All" button
    if (this.hasSelectedAllProducts) {
      return true;
    }
    
    // Otherwise, check if all valid products are selected
    // Get all valid products from the current product list
    const allValidProducts = this.filteredProducts.filter((product: IProduct) => 
      this.isProductValid(product)
    );
    
    // Check if all valid products are selected
    if (allValidProducts.length === 0) return false;
    
    // Check if all valid products are in selectedProductIds
    const allProductsAreSelected = allValidProducts.every((product: IProduct) => {
      const productId = product.id ?? product.product_id;
      return productId != null && this.selectedProductIds.has(productId);
    });
    
    if (!allProductsAreSelected) return false;
    
    // Also check if we've selected all products (total count matches metadata count)
    const totalSelectedCount = this.selectedProductIds.size;
    const totalProductCount = this.productsMetadata?.count ?? 0;
    
    // If we have metadata and selected count matches or exceeds it, all products are selected
    if (totalProductCount > 0 && totalSelectedCount >= totalProductCount) {
      return true;
    }
    
    // If no metadata, just check if all visible valid products are selected
    return allProductsAreSelected;
  }

  isProductSelected(product: IProduct) {
    // Use the same ID logic as parent: id ?? product_id
    const productId = product.id ?? product.product_id;
    if (!productId) return false;
    
    // Check selectedProductIds Set for performance
    return this.selectedProductIds.has(productId);
  }

  /**
   * Auto-select products whose IDs are in the provided Set
   * This is called when products are loaded via scroll/pagination
   */
  autoSelectProductsByIds(selectedIds: Set<number>): void {
    if (!selectedIds || selectedIds.size === 0) return;

    let hasChanges = false;
    const rowKey = (p: IProduct) => p.id ?? p.product_id;
    this.products.filter((product) => rowKey(product) != null && this.isProductValid(product) && selectedIds.has(rowKey(product)!)).forEach((product) => {
      // Check if product is already selected (same row key as parent: id ?? product_id)
      const isAlreadySelected = this.selectedProducts.some(
        (p) => rowKey(p) === rowKey(product)
      );
      if (!isAlreadySelected) {
        // Add product to selectedProducts if not already there
        this.selectedProducts.push(product);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      // Emit changes after a short delay to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.emitSelectedProducts();
      }, 0);
    }
  }

  /**
   * Table header checkbox: selects or deselects only the currently loaded products.
   * When "select all" is active, clicking the checkbox will deselect all.
   */
  toggleAllProducts(checked: boolean) {
    if (!this.canSubmit) return;
    // If "select all" is active and user clicks the checkbox, deselect all
    if (this.hasSelectedAllProducts) {
      this.cancelSelectAll();
      return;
    }
    
    // Normal behavior: select/deselect only currently loaded products
    const allValidProducts = this.filteredProducts.filter((product: IProduct) => this.isProductValid(product));
    this.selectedProducts = checked ? [...allValidProducts] : [];
    
    // If deselecting all, clear the "select all" flag
    if (!checked && this.hasSelectedAllProducts) {
      this.deselectAllRequested.emit();
    }
    
    // Emit changes - parent will update selectedProductIds Set
    this.emitSelectedProducts();
  }

  toggleProduct(product: IProduct, checked: boolean, update = false) {
    if (!this.canSubmit) return;
    if (!this.isProductValid(product)) return;
    
    // Use the same ID logic as parent: id ?? product_id
    const productId = product.id ?? product.product_id;
    if (!productId) return;
    
    if (checked) {
      // Add to selectedProducts if not already there
      const existingIndex = this.selectedProducts.findIndex((p) => (p.id ?? p.product_id) === productId);
      if (existingIndex === -1) {
        this.selectedProducts.push(product);
      } else if (update) {
        // Update existing product in selectedProducts
        this.selectedProducts[existingIndex] = product;
      }
    } else {
      // Remove from selectedProducts
      this.selectedProducts = this.selectedProducts.filter(
        (prod: IProduct) => (prod.id ?? prod.product_id) !== productId
      );
      // If manually deselecting after "select all", notify parent to clear the flag
      if (this.hasSelectedAllProducts) {
        this.deselectAllRequested.emit();
      }
    }

    // Emit changes - parent will update selectedProductIds Set
    this.emitSelectedProducts();
  }

  /**
   * "Select all" button action: selects everything (all loaded products and keeps selecting as more load).
   * Use the table header checkbox for selecting only the currently loaded products.
   */
  selectAllProducts() {
    if (!this.canSubmit) return;
    // If we already have all products loaded, just select them
    const totalCount = this.productsMetadata?.count ?? 0;
    const currentProductsCount = this.filteredProducts.length;

    if (currentProductsCount >= totalCount) {
      console.log('All products already loaded, just select them');
      this.toggleAllProducts(true);
      return;
    }

    this.selectAllRequested.emit();
  }

  cancelSelectAll() {
    if (!this.canSubmit) return;
    // Deselect all products
    this.selectedProducts = [];
    this.selectedProductIds = new Set();
    // Emit changes - parent will update selectedProductIds Set
    this.emitSelectedProducts();
    // Notify parent to stop auto-selecting newly loaded products
    this.deselectAllRequested.emit();
  }

  // Method to select all currently loaded valid products (and/or those whose IDs are in productsIds).
  // When in "select all" mode, parent calls this with each new batch of IDs so products get selected as they load.
  selectAllValidProducts(productsIds: Set<number> | null = null) {
    if (!this.canSubmit) return;
    const idsToAdd = productsIds && productsIds.size > 0
      ? new Set(productsIds)
      : null;

    // Candidates: products in filteredProducts that match idsToAdd (or all if no idsToAdd)
    const candidates = this.filteredProducts.filter((product: IProduct) => {
      const pid = product.id ?? product.product_id;
      if (pid == null || pid <= 0) return false;
      if (!idsToAdd) return true;
      return idsToAdd.has(pid);
    });

    const existingIds = new Set(this.selectedProducts.map((p) => p.id ?? p.product_id).filter((id): id is number => id != null && id > 0));
    const newProducts = candidates.filter((p) => !existingIds.has(p.id ?? p.product_id!));

    // Ensure each new product has final prices per currency and form controls before adding (fixes "load more" not setting prices)
    const discountDefault = this.newCatalogPricingRules?.overall_pricing_value ?? 0;
    newProducts.forEach((product: IProduct) => {
      this.initializeProductCurrencies(product);
      const discount = product.discount ?? discountDefault;
      this.updateAllCurrenciesFinalPrice(product, discount);
      product.discount = this.normalizeDiscount(discount);
      this.createControlsForProduct(product);
    });

    // Add only products that are valid after ensuring controls and currencies
    const validNewProducts = newProducts.filter((p) => this.isProductValid(p));
    if (validNewProducts.length > 0) {
      this.selectedProducts = [...this.selectedProducts, ...validNewProducts];
      this.selectedProductIds = new Set(this.selectedProducts.map((p) => p.id ?? p.product_id!).filter((id): id is number => id != null && id > 0));
    }

    // Emit changes - parent will update selectedProductIds Set
    this.emitSelectedProducts();
  }

  getCurrencySymbol(): string {
    if (this.source === 'agents') {
      return '€';
    } else {
      return this.availableCurrencies.find((c) => c.id === this.currencyControl.value)?.symbol || '';
    }
  }

  isProductValid(product: IProduct): boolean {
    if (this.options.triggerPage === 'catalog-view') return true;
    if (product.status === 'Draft') return false;
    if (!product.enabled) return false;

    const formId = this.getProductFormId(product);
    if (formId == null) return false;
    const finalPriceCtrl = this.productsForm.get(`final_price_${formId}`);
    const discountCtrl = this.productsForm.get(`discount_${formId}`);

    if (this.newCatalogPricingRules?.extra_discount) {
      const extraDiscountCtrl = this.productsForm.get(`extra_discount_${formId}`);

      return !!finalPriceCtrl && finalPriceCtrl.valid && !!discountCtrl && discountCtrl.valid && !!extraDiscountCtrl && extraDiscountCtrl.valid;
    }

    return !!finalPriceCtrl && finalPriceCtrl.valid;
  }

  /**
   * Get total count of selected products (including those not currently displayed)
   * Calculates from selectedProducts array to ensure accuracy when manually deselecting
   */
  getTotalSelectedCount(): number {
    // Calculate from selectedProducts array to ensure it's always accurate
    // This handles cases where selectedProductIds @Input might not be updated yet
    const uniqueIds = new Set(
      this.selectedProducts
        .map(p => p.id ?? p.product_id ?? 0)
        .filter(id => id > 0)
    );
    return uniqueIds.size;
  }

  emitSelectedProducts() {
    const isMarginMode = this.newCatalogPricingRules.pricing_rule === 'margin';
    const products: IProduct[] = this.selectedProducts.map((product: IProduct) => {
      const formId = this.getProductFormId(product);
      let final_price = formId != null ? (this.productsForm.get(`final_price_${formId}`)?.value || this.getCurrencyPrice(product, 'final_price') || 0) : (this.getCurrencyPrice(product, 'final_price') || 0);
      const discount = formId != null ? (this.productsForm.get(`discount_${formId}`)?.value ?? product.discount ?? this.newCatalogPricingRules.overall_pricing_value ?? 0) : (product.discount ?? this.newCatalogPricingRules.overall_pricing_value ?? 0);
      const extra_discount = formId != null ? this.productsForm.get(`extra_discount_${formId}`)?.value ?? product.extra_discount : product.extra_discount;

      if (isMarginMode) {
        const cost = this.getCurrencyCost(product) || product.cost || 0;
        if (cost && cost > 0 && discount !== undefined && discount !== null) {
          final_price = Number((cost * (1 + discount / 100)).toFixed(2));
        }
      }

      // Update the product with currency-specific data
      if (product.currencies) {
        const catalogData = this.catalogService.getNewCatalogData();
        const enabledCurrencyIds = catalogData?.enabled_currencies || [];
        const defaultCurrencyId = catalogData?.default_currency;
        
        if (isMarginMode) {
          product.currencies = product.currencies.map((currency: IProductCurrency) => {
            if (enabledCurrencyIds.includes(currency.currency_id!) || currency.currency_id === defaultCurrencyId) {
              const currencyCost = currency.cost ?? product.cost ?? 0;
              if (currencyCost && currencyCost > 0 && discount !== undefined && discount !== null) {
                const calculatedFinalPrice = Number((currencyCost * (1 + discount / 100)).toFixed(2));
                return {
                  ...currency,
                  final_price: calculatedFinalPrice
                };
              } else {
                const formFinalPrice = formId != null ? this.productsForm.get(`final_price_${formId}`)?.value : undefined;
                if (formFinalPrice !== null && formFinalPrice !== undefined && formFinalPrice > 0) {
                  return {
                    ...currency,
                    final_price: Number(formFinalPrice)
                  };
                } else if (final_price > 0) {
                  return {
                    ...currency,
                    final_price: Number(final_price)
                  };
                }
              }
            }
            return currency;
          });
          
          const defaultCurrency = product.currencies.find(c => c.currency_id === defaultCurrencyId);
          if (defaultCurrency?.final_price !== undefined && defaultCurrency?.final_price !== null) {
            final_price = defaultCurrency.final_price;
          }
        } else {
          if (this.source === 'catalogs') {
            this.setCurrencyPrice(product, 'final_price', final_price);
          } else {
            product.currencies = product.currencies.map((currency: IProductCurrency) => {
              if (enabledCurrencyIds.includes(currency.currency_id!) || currency.currency_id === defaultCurrencyId) {
                const currencySalesPrice = currency.sales_price ?? product.sales_price ?? 0;
                if (currencySalesPrice > 0 && discount !== undefined && discount !== null) {
                  const calculatedFinalPrice = Number((currencySalesPrice * (1 - discount / 100)).toFixed(2));
                  return {
                    ...currency,
                    final_price: calculatedFinalPrice
                  };
                } else {
                  return {
                    ...currency,
                    final_price: final_price
                  };
                }
              }
              return currency;
            });
          }
        }
      }

      return {
        ...product,
        final_price,
        discount: discount,
        extra_discount,
        hasCustomDiscount: (product as any).hasCustomDiscount,
      };
    });
    this.selectedProductsChange.emit(products);
    this.areSelectedProductsValidEmitter.emit(this.areSelectedProductsValid());
  }

  showDetails(product: IProduct) {
    const catalogData = this.catalogService.getNewCatalogData();

    let currentProduct = product;
    const selectedProduct = this.selectedProducts.find(p => p.id === product.id);
    if (selectedProduct) {
      currentProduct = selectedProduct;
    } else {
      const filteredProduct = this.filteredProducts.find(p => p.id === product.id);
      if (filteredProduct) {
        currentProduct = filteredProduct;
      }
    }

    const dialogRef = this.dialog.open(ProductDetailsDialogComponent, {
      data: { 
        product: currentProduct,
        catalog: catalogData,
        context: this.options?.triggerPage,
        canSubmit: this.canSubmit,
      },
      position: { right: '0' },
      height: '100vh',
      minWidth: '600px',
      panelClass: 'custom-side-dialog',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && currentProduct) {
        const enabledHandlingUnitIds = result.enabled_handling_unit_ids || [];
        const defaultHandlingUnitId = result.default_handling_unit_id || null;
        
        const updatedHandlingUnits = (currentProduct.handling_units || []).map((hu: any) => {
          const huTypeId = hu.type_id;
          const isEnabled = enabledHandlingUnitIds.includes(huTypeId);
          const isDefault = defaultHandlingUnitId === huTypeId;
          
          return {
            ...hu,
            enabled: isEnabled,
            default: isDefault
          };
        });

        const productIndex = this.selectedProducts.findIndex(p => p.id === currentProduct.id);
        
        let updatedProduct: IProduct;
        if (productIndex !== -1) {
          const originalProduct = this.selectedProducts[productIndex];
          updatedProduct = {
            ...originalProduct,
            handling_units: updatedHandlingUnits,
            enabled_handling_unit_ids: enabledHandlingUnitIds,
            default_handling_unit_id: defaultHandlingUnitId
          } as any;
          
          this.selectedProducts[productIndex] = updatedProduct;
          this.selectedProductsChange.emit([...this.selectedProducts]);
        } else {
          updatedProduct = {
            ...currentProduct,
            handling_units: updatedHandlingUnits,
            enabled_handling_unit_ids: enabledHandlingUnitIds,
            default_handling_unit_id: defaultHandlingUnitId
          } as any;
        }

        const filteredIndex = this.filteredProducts.findIndex(p => p.id === currentProduct.id);
        if (filteredIndex !== -1) {
          this.filteredProducts[filteredIndex] = updatedProduct;
        }

        const productsIndex = this.products.findIndex(p => p.id === currentProduct.id);
        if (productsIndex !== -1) {
          this.products[productsIndex] = updatedProduct;
        }
      }
      
      const isValid = this.areSelectedProductsValid();
      this.areSelectedProductsValidEmitter.emit(isValid);
    });
  }

  openOverride(element: IProduct) {
    if (!this.canSubmit) return;
    const dialogRef = this.dialog.open(CustomeOverrideComponent, {
      data: element,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        const updated: IProduct = {
          ...element,
          increment: result.increment != null && result.increment !== '' ? +result.increment : element.increment,
          min_qte: result.min_qte != null && result.min_qte !== '' ? +result.min_qte : element.min_qte,
          max_qte: result.max_qte != null && result.max_qte !== '' ? +result.max_qte : element.max_qte,
          can_be_sold_out_of_stock:
            result.can_be_sold_out_of_stock !== undefined ? result.can_be_sold_out_of_stock : element.can_be_sold_out_of_stock,
          is_override: true,
        };

        this.products = this.products.map((p) => (p.product_id === updated.product_id ? updated : p));
        this.filteredProducts = this.filteredProducts.map((p) => (p.product_id === updated.product_id ? updated : p));
        this.toggleProduct(updated, true, true);
      }
    });
  }

  private getSelectedProductsWithFormValues(): IProduct[] {
    return this.selectedProducts.map((product: IProduct) => {
      const formId = this.getProductFormId(product);
      const final_price = formId != null ? (this.productsForm.get(`final_price_${formId}`)?.value ?? (this.source === 'agents' ? product.final_price : this.getCurrencyPrice(product, 'final_price')) ?? null) : ((this.source === 'agents' ? product.final_price : this.getCurrencyPrice(product, 'final_price')) ?? null);
      
      return {
        ...product,
        final_price: final_price,
      };
    });
  }

  areSelectedProductsValid(): boolean {
    const pricingRule = this.newCatalogPricingRules.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp';
    return areSelectedProductsValid(
      this.selectedProducts, 
      this.availableCurrencies, 
      this.translateService, 
      this.source,
      pricingRule,
      this.defaultCurrency
    );
  }

  getValidationTooltipMessage(): string {
    const pricingRule = this.newCatalogPricingRules.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp';
    return getValidationTooltipMessage(
      this.selectedProducts, 
      this.availableCurrencies, 
      this.translateService, 
      this.source,
      pricingRule,
      this.defaultCurrency
    );
  }

  getGlobalValidationMessage(): string {
    const pricingRule = this.newCatalogPricingRules.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp';
    return getGlobalValidationMessage(
      this.selectedProducts, 
      this.availableCurrencies, 
      this.translateService, 
      this.source,
      pricingRule,
      this.defaultCurrency
    );
  }

  getMissingFinalPriceCurrencies(product: IProduct): string[] {
    if (!this.isMultiCurrency || !this.availableCurrencies.length) {
      return [];
    }

    const missingCurrencies: string[] = [];
    
    this.availableCurrencies.forEach((availableCurrency) => {
      const currency = product.currencies?.find((c: any) => c.currency_id === availableCurrency.id);
      const finalPrice = currency?.final_price;
      
      if (finalPrice === null || finalPrice === undefined) {
        missingCurrencies.push(availableCurrency.name || `Currency ${availableCurrency.id}`);
      }
    });

    return missingCurrencies;
  }

  getMissingFinalPriceCurrenciesMessage(product: IProduct): string {
    const missingCurrencies = this.getMissingFinalPriceCurrencies(product);
    
    if (missingCurrencies.length === 0) {
      return '';
    }

    if (missingCurrencies.length === 1) {
      return this.translateService.instant('Catalogs.new_catalog.products.listing.final_price_missing_single_currency', {
        currency: missingCurrencies[0]
      });
    }

    const currenciesList = missingCurrencies.join(', ');
    return this.translateService.instant('Catalogs.new_catalog.products.listing.final_price_missing_multiple_currencies', {
      currencies: currenciesList,
      count: missingCurrencies.length
    });
  }

  hasMissingFinalPriceForAnyCurrency(product: IProduct): boolean {
    if (!this.isMultiCurrency) {
      const formId = this.getProductFormId(product);
      const finalPriceCtrl = formId != null ? this.productsForm.get(`final_price_${formId}`) : null;
      return !finalPriceCtrl || finalPriceCtrl.value === null || finalPriceCtrl.value === undefined;
    }
    
    return this.getMissingFinalPriceCurrencies(product).length > 0;
  }

  getZeroFinalPriceMessage(product: IProduct): string {
    if (this.isMultiCurrency && this.currencyControl.value) {
      const currency = this.availableCurrencies.find(c => c.id === this.currencyControl.value);
      if (currency) {
        return this.translateService.instant('Catalogs.new_catalog.products.listing.final_price_zero_with_currency', {
          currency: currency.name
        });
      }
    }
    return this.translateService.instant('Catalogs.new_catalog.products.listing.final_price_zero');
  }

  getFieldError(fieldName: string): string {
    const control = this.productsForm.get(fieldName);
    if (!control || !control.invalid || (!control.touched && !control.dirty)) {
      return '';
    }

    if (control.hasError('required')) {
      return this.translateService.instant('common.required_field');
    }
    if (control.hasError('min')) {
      return this.translateService.instant('Catalogs.new_catalog.validation.value.min');
    }
    if (control.hasError('max')) {
      return this.translateService.instant('Catalogs.new_catalog.validation.value.max');
    }
    if (control.hasError('pattern')) {
      return this.translateService.instant('common.invalid_format');
    }

    return '';
  }

  hasFieldError(fieldName: string): boolean {
    const control = this.productsForm.get(fieldName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  private updateHasMore(): void {
    if (this.productsMetadata) {
      this.hasMore = this.filteredProducts.length < this.productsMetadata.count;
    } else {
      this.hasMore = false;
    }
  }

  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  private handleScroll(event: Event): void {
    const now = Date.now();
    if (now - this.lastScrollEmit < 500) {
      return;
    }

    // Prevent loading more while selecting all or already loading
    if (this.isSelectingAll || this.isLoadingMore || !this.hasMore) {
      return;
    }

    // For cdk-virtual-scroll-viewport, the event target is the viewport element
    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    // Get scroll measurements
    const scrollTop = target.scrollTop || 0;
    const clientHeight = target.clientHeight || 0;
    const scrollHeight = target.scrollHeight || 0;

    // Calculate scroll position
    const scrollPosition = scrollTop + clientHeight;
    
    // Check if we're near the bottom (within threshold)
    if (scrollHeight > 0 && scrollPosition / scrollHeight > this.scrollThreshold) {
      this.lastScrollEmit = now;
      this.loadMore.emit();
    }
  }

  /** Run change detection so selection state (e.g. checkboxes) updates after parent updates selectedProductIds. */
  runChangeDetection(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.scrollSubject.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
